import React, { useState, useEffect } from "react";
import { TaxReturn, User } from "@/entities/all";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { createPageUrl } from "@/utils";

import PersonalInfoStep from "../components/interview/PersonalStep.jsx";
import IncomeStep from "../components/interview/IncomeStep.jsx";
import DeductionsStep from "../components/interview/DeductionStep.jsx";
import ReviewStep from "../components/interview/ReviewStep.jsx";

const STEPS = [
  { id: 'personal', title: 'Personal Information', description: 'Basic info and filing status' },
  { id: 'income', title: 'Income Sources', description: 'W-2s, 1099s, and other income' },
  { id: 'deductions', title: 'Deductions & Credits', description: 'Itemized or standard deductions' },
  { id: 'review', title: 'Review', description: 'Verify all information' }
];

export default function Interview() {
  const [currentStep, setCurrentStep] = useState(0);
  const [taxReturn, setTaxReturn] = useState(null);
  const [formData, setFormData] = useState({
    personal_info: {},
    income_info: {},
    deductions: { standard_deduction: true }
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    initializeTaxReturn();
  }, []);

  const initializeTaxReturn = async () => {
    try {
      const user = await User.me();
      const currentYear = new Date().getFullYear();
      
      const returns = await TaxReturn.filter({ tax_year: currentYear }, '-created_date', 1);
      
      if (returns.length > 0) {
        const existingReturn = returns[0];
        setTaxReturn(existingReturn);
        setFormData({
          personal_info: existingReturn.personal_info || {},
          income_info: existingReturn.income_info || {},
          deductions: existingReturn.deductions || { standard_deduction: true }
        });
      } else {
        const newReturn = await TaxReturn.create({
          tax_year: currentYear,
          state: 'CA',
          status: 'draft',
          personal_info: {
            first_name: user.full_name?.split(' ')[0] || '',
            last_name: user.full_name?.split(' ').slice(1).join(' ') || ''
          }
        });
        setTaxReturn(newReturn);
        setFormData({
          personal_info: newReturn.personal_info || {},
          income_info: {},
          deductions: { standard_deduction: true }
        });
      }
    } catch (error) {
      console.error("Error initializing tax return:", error);
    }
  };

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      await saveProgress();
      setCurrentStep(currentStep + 1);
    } else {
      await completeReturn();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const saveProgress = async () => {
    if (!taxReturn) return;
    
    setIsSaving(true);
    try {
      const updatedStatus = currentStep === STEPS.length - 1 ? 'review' : 'in_progress';
      await TaxReturn.update(taxReturn.id, {
        ...formData,
        status: updatedStatus
      });
    } catch (error) {
      console.error("Error saving progress:", error);
    }
    setIsSaving(false);
  };

  const completeReturn = async () => {
    if (!taxReturn) return;
    
    setIsSaving(true);
    try {
      const totalIncome = Object.values(formData.income_info || {}).reduce((sum, val) => sum + (val || 0), 0);
      const deductionAmount = formData.deductions?.standard_deduction ? 13850 : 
        Object.values(formData.deductions || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
      
      const taxableIncome = Math.max(0, totalIncome - deductionAmount);
      const federalTax = Math.round(taxableIncome * 0.22);
      const stateTax = Math.round(taxableIncome * 0.05);
      
      await TaxReturn.update(taxReturn.id, {
        ...formData,
        status: 'completed',
        calculated_tax: {
          federal_tax: federalTax,
          state_tax: stateTax,
          total_tax: federalTax + stateTax,
          refund_owed: 0
        }
      });
      
      window.location.href = createPageUrl("Forms");
    } catch (error) {
      console.error("Error completing return:", error);
    }
    setIsSaving(false);
  };

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data }
    }));
  };

  const renderCurrentStep = () => {
    const stepId = STEPS[currentStep].id;

    switch (stepId) {
      case 'personal':
        return <PersonalInfoStep data={formData.personal} onUpdate={(data) => updateFormData('personal', data)} />;
      case 'income':
        return <IncomeStep data={formData.income} onUpdate={(data) => updateFormData('income', data)} />;
      case 'deductions':
        return <DeductionsStep data={formData.deductions} onUpdate={(data) => updateFormData('deductions', data)} />;
      case 'review':
        return <ReviewStep data={formData} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading interview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Interview</h1>
          <p className="text-gray-600">Let's gather your tax information step by step</p>
        </div>

        <Card className="shadow-lg mb-6">
          <CardContent className="p-8">
            {renderCurrentStep()}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            Previous
          </Button>
          <Button
            onClick={handleNext}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {currentStep === STEPS.length - 1 ? 'Complete' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}